<template>
  <div class="chat-container">
    <!-- Chat icon -->
    <div v-if="!showChat" class="chat-icon" @click="toggleChat">
      <i class="fas fa-comments"></i>      
    </div>

    <!-- Chat popup -->
    <div v-if="showChat" class="chat-popup">
      <div class="chat-header">
        <i class="fas fa-comments chat-header-icon"></i>
        Contact Us
        <a class="close-btn" href="javascript:" @click="toggleChat">
        <i class="fa fa-times"></i>
      </a>
      </div>

      <div class="chat-history">
        <div v-for="message in messages" :key="message.id" :class="['chat-message', message.sender]">
          <div class="message-content">{{ message.content }}</div>
        </div>
      </div>

      <div class="chat-input">
        <input v-model="userInput" type="text" placeholder="Write a message..." @keyup.enter="pushMessage" />
        <button @click="pushMessage"><i class="fas fa-paper-plane"></i></button>
      </div>
    </div>
  </div>
</template>

  <script setup>
import { ref, nextTick, onMounted, onBeforeUnmount } from 'vue';
import APIClient from '~/api/apiClient.ts';
import { useRouter, useRoute } from 'vue-router';

const toggleChat = () => {
  showChat.value = !showChat.value;
};

const lastSentMessageId = ref(-1);

const userInput = ref('');
const messages = ref([
  { id: 0, content: 'Hello there. How can I help you?', sender: 'bot' },
  // ... Additional messages as they are sent or received
]);

const maxUserMessages = 6;
const showChat = ref(false);
const conversationId = ref('');
const chatClosed = ref(false);

const pushMessage = async () => {
  if (chatClosed.value || userInput.value.trim() === '') {
    console.log('Chat is closed or user input is empty');
    return;
  }

  // Append user's message to the chat
  const newMessage = {
    id: messages.value.length,
    content: userInput.value,
    sender: 'user'  // Assuming 'user' for user messages, 'bot' for bot responses
  };
  messages.value.push(newMessage);
  console.log('User message pushed:', newMessage);

  // Clear input field after sending a message
  userInput.value = '';

  // Prepare the full conversation for the API request
  const conversationHistory = messages.value.map(message => ({
    role: message.sender,
    content: message.content
  }));

  try {
    console.log('Sending request to GPT API');
    const apiResponse = await fetch(`${import.meta.env.VITE_WP_BASE_URL}/wp-json/fa/v1/handle-live-chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': import.meta.env.VITE_WP_TOKEN
      },
      body: JSON.stringify({ messages: conversationHistory }) // Send the full conversation
    });

    // Process the response here as needed

    if (!apiResponse.ok) {
      console.error('API response was not ok', apiResponse.status);
      throw new Error(`API response was not ok. Status: ${apiResponse.status}`);
    }

    const responseData = await apiResponse.json();
    console.log('API response received:', responseData);

    if (responseData.error) {
      throw new Error(responseData.error);
    }

    // Check for specific actions based on the response
    if (responseData.action === 'send_message' && responseData.message) {
      // Remove "Bot:" from the response message
      const cleanedMessage = responseData.message.replace(/Bot:\s*/g, '');

      // Append the AI response to the chat
      messages.value.push({
        id: messages.value.length,
        content: cleanedMessage,
        sender: 'bot'
      });
    } else if (responseData.action === 'close_chat') {
      chatClosed.value = true; // Close the chat UI
      console.log('Chat closed by server command.');
      messages.value.push({
        id: messages.value.length,
        content: 'The chat has been closed by the server.',
        sender: 'bot'
      });
    } else if (responseData.action === 'contact_dev_team') {
      chatClosed.value = true;
      console.log('Development team has been contacted.');
      messages.value.push({
        id: messages.value.length,
        content: 'Our development team has been contacted and will respond to your query shortly. Thank you for your assistance. This chat is now closed.',
        sender: 'bot'
      });
    } else {
      // Handle unexpected or missing action
      throw new Error('Unexpected response action or missing text');
    }
  }  catch (error) {
    console.error('Error during live chat:', error);
    messages.value.push({
      id: messages.value.length,
      content: 'Sorry, there was a problem with the Chat. Please try again later.',
      sender: 'bot'
    });
  }

  // Scroll chat to the latest message
  await nextTick();
  const chatHistoryElement = document.querySelector('.chat-history');
  chatHistoryElement.scrollTop = chatHistoryElement.scrollHeight;
  console.log('Chat scrolled to latest message');
};


  </script>

  <style lang="scss" scoped>
  @import '~/assets/constants.scss';

  .chat-container {
  // Existing styles for chat-container...

  // Ensure the chat icon remains in the corner when the chat popup is closed
  position: fixed;
  bottom: 8rem; // Adjust as needed
  right: 1rem; // Adjust as needed
  z-index: 1000; // Ensure it's above other elements
}

  .chat-popup {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
  width: 450px;
  position: fixed;
  bottom: 8rem;
  right: 1rem;
  font-family: 'Arial', sans-serif;
  z-index: 100;
  display: flex;
  flex-direction: column;
  overflow: hidden; // Prevents children from overflowing the rounded corners
  @include respond-to("phone-only") {
        right: 0;
      }
}


.chat-icon {
  // Styles to make it appear as a circular button
  background-color: $fa-orange; // Or any color you prefer
  color: #fff;
  border-radius: 50%;
  width: 60px; // Size of the circle
  height: 60px; // Size of the circle
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  box-shadow: 0 2px 10px 0 rgba(0,0,0,0.2);
  cursor: pointer;

  // Ensure that the chat icon is not partially hidden by the overflow of chat-container
  overflow: visible;
}
.chat-header {
  background-color: $fa-orange; // Use the variable for the orange color from your constants.scss
  color: #fff;
  padding: 10px 15px;
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: bold;
  
  .chat-header-icon {
    font-size: 1.5rem; // Adjust as needed
    margin-right: 8px; // Spacing between icon and text
  }
}

// Additional styles for messages and the end message
.showResponse, .showEndMessage {
  padding: 10px 15px;
}

  
.chat-history {
  flex-grow: 1;
  overflow-y: auto;
  padding: 10px;
  display: flex;
  flex-direction: column; // Newest messages at the bottom
}

.chat-message {
  max-width: 80%;
  margin-bottom: 10px;
  padding: 10px;
  border-radius: 20px;
  &.user {
    align-self: flex-start;
    background-color: #d0e0fc; // Light blue for the user messages
  }
  &.bot {
    align-self: flex-end;
    background-color: #f1f0f0; // Light gray for the bot messages
  }
}

.message-content {
  word-wrap: break-word;
}

.chat-input {
  display: flex;
  padding: 10px;
  border-top: 1px solid #ccc;

  input {
    flex-grow: 1;
    border: none;
    padding: 10px;
    margin-right: 10px;
  }

  button {
    padding: 10px;
    border: none;
    background-color: $fa-blue;
    border-radius: $fa-border-radius;
    color: white;
    cursor: pointer;

    i {
      font-size: 1rem;
    }

    &:hover {
      background-color: darken($fa-blue, 10%);
    }
  }
}
</style>